import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
} from '@mui/material';
import { Add as AddIcon, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import AccountantLayout from '../components/AccountantLayout';

const CalendarPage = () => {
  const { t } = useTranslation();
  const [currentMonth] = useState('June 2026');

  const events = [
    { id: 1, date: '2026-07-03', title: 'Client Meeting - Seabreeze Properties', time: '10:00 AM', type: 'Meeting' },
    { id: 2, date: '2026-07-05', title: 'Q2 Financial Review - Atlas Construction', time: '2:00 PM', type: 'Review' },
    { id: 3, date: '2026-07-08', title: 'Monthly Reconciliation - Orchard Foods', time: '9:00 AM', type: 'Task' },
    { id: 4, date: '2026-07-12', title: 'VAT Return Deadline - Meridian Logistics', time: 'All Day', type: 'Deadline' },
    { id: 5, date: '2026-07-15', title: 'Audit Documentation - Helix Software', time: '11:00 AM', type: 'Task' },
    { id: 6, date: '2026-07-19', title: 'VAT Returns Due', time: 'All Day', type: 'Deadline' },
  ];

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'Meeting':
        return { bg: '#E3F2FD', color: '#1976D2' };
      case 'Review':
        return { bg: '#F3E5F5', color: '#9C27B0' };
      case 'Task':
        return { bg: '#E8F5E9', color: '#4CAF50' };
      case 'Deadline':
        return { bg: '#FFEBEE', color: '#F44336' };
      default:
        return { bg: '#F5F5F5', color: '#666' };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <AccountantLayout>
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('accountant_calendar')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('schedule_events')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
              },
            }}
          >
            {t('new_event')}
          </Button>
        </Box>

        <Paper sx={{ p: { xs: 1.5, sm: 3 }, mb: 3, borderRadius: '12px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 1 }}>
            <Button startIcon={<NavigateBefore />} sx={{ textTransform: 'none' }}>
              {t('previous')}
            </Button>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {currentMonth}
            </Typography>
            <Button endIcon={<NavigateNext />} sx={{ textTransform: 'none' }}>
              {t('next')}
            </Button>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 1,
              mb: 3,
            }}
          >
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <Box
                key={day}
                sx={{
                  p: 1,
                  textAlign: 'center',
                  fontWeight: 'bold',
                  color: 'text.secondary',
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 1,
            }}
          >
            {Array.from({ length: 30 }, (_, i) => i + 1).map((day) => (
              <Box
                key={day}
                sx={{
                  p: { xs: 0.75, sm: 2 },
                  minHeight: { xs: 56, sm: 80 },
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(102, 126, 234, 0.04)',
                    borderColor: '#667eea',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: '12px' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
            {t('upcoming_events')}
          </Typography>
          <Grid container spacing={2}>
            {events.map((event) => {
              const typeColor = getEventTypeColor(event.type);
              return (
                <Grid item xs={12} md={6} key={event.id}>
                  <Card
                    sx={{
                      borderRadius: '8px',
                      borderLeft: `4px solid ${typeColor.color}`,
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      },
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                          {event.title}
                        </Typography>
                        <Chip
                          label={event.type}
                          size="small"
                          sx={{
                            backgroundColor: typeColor.bg,
                            color: typeColor.color,
                            fontWeight: 500,
                          }}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        📅 {formatDate(event.date)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        🕐 {event.time}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      </Box>
    </AccountantLayout>
  );
};

export default CalendarPage;
